import LRUCache from "@jsr/fiatjaf__lru-cache/lru-cache";
/**
 * A `DataLoader` creates a public API for loading data from a particular
 * data back-end with unique keys such as the `id` column of a SQL table or
 * document name in a MongoDB database, given a batch loading function.
 *
 * Each `DataLoader` instance contains a unique memoized cache. Use caution when
 * used in long-lived applications or those which serve many users with
 * different access permissions and consider creating a new instance per
 * web request.
 */ class DataLoader {
  // Private
  _batchLoadFn;
  _maxBatchSize;
  _cacheKeyFn;
  _cacheMap;
  _batch;
  constructor(batchLoadFn, options){
    if (typeof batchLoadFn !== 'function') {
      throw new TypeError('DataLoader must be constructed with a function which accepts ' + `Array<key> and returns Promise<Array<value>>, but got: ${batchLoadFn}.`);
    }
    this._batchLoadFn = batchLoadFn;
    this._maxBatchSize = options?.maxBatchSize || Infinity;
    this._cacheKeyFn = options?.cacheKeyFn;
    const _cache = new LRUCache(2000);
    this._cacheMap = {
      get: options.transformCacheHit ? (key)=>_cache.get(key)?.then(options.transformCacheHit) : (key)=>_cache.get(key),
      set (key, value) {
        return _cache.set(key, value);
      },
      delete (key) {
        _cache.set(key, undefined);
      },
      clear () {
        _cache.clear();
      }
    };
    this._batch = null;
  }
  /**
   * Loads a key, returning a `Promise` for the value represented by that key.
   */ load(key) {
    if (key === null || key === undefined) {
      throw new TypeError('The loader.load() function must be called with a value, ' + `but got: ${String(key)}.`);
    }
    const batch = getCurrentBatch(this);
    const cacheKey = this._cacheKeyFn(key);
    // If caching and there is a cache-hit, return cached Promise.
    const cachedPromise = this._cacheMap.get(cacheKey);
    if (cachedPromise) {
      const cacheHits = batch.cacheHits || (batch.cacheHits = []);
      return new Promise((resolve)=>{
        cacheHits.push(()=>{
          resolve(cachedPromise);
        });
      });
    }
    // Otherwise, produce a new Promise for this key, and enqueue it to be
    // dispatched along with the current batch.
    batch.keys.push(key);
    const promise = new Promise((resolve, reject)=>{
      batch.callbacks.push({
        resolve,
        reject
      });
    });
    // If caching, cache this promise.
    this._cacheMap.set(cacheKey, promise);
    return promise;
  }
  /**
   * Clears the value at `key` from the cache, if it exists. Returns itself for
   * method chaining.
   */ clear(key) {
    const cacheMap = this._cacheMap;
    if (cacheMap) {
      const cacheKey = this._cacheKeyFn(key);
      cacheMap.delete(cacheKey);
    }
    return this;
  }
}
// Private: Either returns the current batch, or creates and schedules a
// dispatch of a new batch for the given loader.
function getCurrentBatch(loader) {
  // If there is an existing batch which has not yet dispatched and is within
  // the limit of the batch size, then return it.
  const existingBatch = loader._batch;
  if (existingBatch !== null && !existingBatch.hasDispatched && existingBatch.keys.length < loader._maxBatchSize) {
    return existingBatch;
  }
  // Otherwise, create a new batch for this loader.
  const newBatch = {
    hasDispatched: false,
    keys: [],
    callbacks: []
  };
  // Store it on the loader so it may be reused.
  loader._batch = newBatch;
  // Then schedule a task to dispatch this batch of requests.
  setTimeout(()=>{
    dispatchBatch(loader, newBatch);
  });
  return newBatch;
}
function dispatchBatch(loader, batch) {
  // Mark this batch as having been dispatched.
  batch.hasDispatched = true;
  // If there's nothing to load, resolve any cache hits and return early.
  if (batch.keys.length === 0) {
    resolveCacheHits(batch);
    return;
  }
  // Call the provided batchLoadFn for this loader with the batch's keys and
  // with the loader as the `this` context.
  let batchPromise;
  try {
    batchPromise = loader._batchLoadFn(batch.keys);
  } catch (e) {
    return failedDispatch(loader, batch, new TypeError('DataLoader must be constructed with a function which accepts ' + 'Array<key> and returns Promise<Array<value>>, but the function ' + `errored synchronously: ${String(e)}.`));
  }
  // Assert the expected response from batchLoadFn
  if (!batchPromise || typeof batchPromise.then !== 'function') {
    return failedDispatch(loader, batch, new TypeError('DataLoader must be constructed with a function which accepts ' + 'Array<key> and returns Promise<Array<value>>, but the function did ' + `not return a Promise: ${String(batchPromise)}.`));
  }
  // Await the resolution of the call to batchLoadFn.
  batchPromise.then((values)=>{
    if (values.length !== batch.keys.length) {
      throw new TypeError('DataLoader must be constructed with a function which accepts ' + 'Array<key> and returns Promise<Array<value>>, but the function did ' + 'not return a Promise of an Array of the same length as the Array ' + 'of keys.' + `\n\nKeys:\n${String(batch.keys)}` + `\n\nValues:\n${String(values)}`);
    }
    // Resolve all cache hits in the same micro-task as freshly loaded values.
    resolveCacheHits(batch);
    // Step through values, resolving or rejecting each Promise in the batch.
    for(let i = 0; i < batch.callbacks.length; i++){
      const value = values[i];
      if (value instanceof Error) {
        batch.callbacks[i].reject(value);
      } else {
        batch.callbacks[i].resolve(value);
      }
    }
  }).catch((error)=>{
    failedDispatch(loader, batch, error);
  });
}
// Private: do not cache individual loads if the entire batch dispatch fails,
// but still reject each request so they do not hang.
function failedDispatch(loader, batch, error) {
  // Cache hits are resolved, even though the batch failed.
  resolveCacheHits(batch);
  for(let i = 0; i < batch.keys.length; i++){
    loader.clear(batch.keys[i]);
    batch.callbacks[i].reject(error);
  }
}
// Private: Resolves the Promises for any cache hits in this batch.
function resolveCacheHits(batch) {
  if (batch.cacheHits) {
    for(let i = 0; i < batch.cacheHits.length; i++){
      batch.cacheHits[i]();
    }
  }
}
export default DataLoader;
//# sourceMappingURL=dataloader.js.map